import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import { toast } from "react-toastify";
import ChecklistResources from "../resources/ChecklistResources";
import {ACCESS_DENIED} from '../Messages'
import { loginSuccess, logout } from "../redux/actions/LoggedInAction";
import { connect } from "react-redux";

const POST_PRODUCTION = 'Post Production'

function DonutChart({ percentage, label = 'Ready', color = '#35ab7c', bg = '#dff3ea' }) {
    const clampedPct = Math.min(100, Math.max(0, percentage));
    const radius = 90;
    const stroke = 18;
    const center = 110;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (clampedPct / 100) * circumference;

    return (
        <svg viewBox="0 0 220 220" style={{width: '100%', maxWidth: 'min(100%, 360px)', display: 'block', margin: '0 auto'}}>
            <circle cx={center} cy={center} r={radius} fill="none" stroke={bg} strokeWidth={stroke} />
            <circle cx={center} cy={center} r={radius} fill="none" stroke={color} strokeWidth={stroke}
                strokeDasharray={circumference} strokeDashoffset={offset}
                strokeLinecap="round" transform={`rotate(-90 ${center} ${center})`}
                style={{transition: 'stroke-dashoffset 0.5s ease'}} />
            <circle cx={center} cy={center} r={radius - stroke / 2} fill={bg} />
            <text x={center} y={center - 8} textAnchor="middle" dominantBaseline="central"
                style={{fontSize: '48px', fontWeight: 'bold', fill: color}}>{clampedPct}%</text>
            <text x={center} y={center + 32} textAnchor="middle" dominantBaseline="central"
                style={{fontSize: '20px', fill: '#666'}}>{label}</text>
        </svg>
    );
}

function ProductionReady({isLoggedIn, loggedInUser, logout}){
    const resources = new ChecklistResources()

    const [checkedItems, setCheckedItems] = useState([]);
    const [categorizedItems, setCategorizedItems] = useState({})
    const [preItems, setPreItems] = useState([])
    const [postItems, setPostItems] = useState([])

    useEffect(() => {
        resources.get().then(response => {
            const all = response['body'] || []

            const pre = all.filter(i => i.item_classification !== POST_PRODUCTION)
            const post = all.filter(i => i.item_classification === POST_PRODUCTION)
            setPreItems(pre)
            setPostItems(post)

            const grouped = {}
            all.forEach(item => {
                const cat = item.item_classification || 'Uncategorized'
                if (!grouped[cat]) grouped[cat] = []
                grouped[cat].push(item)
            })
            setCategorizedItems(grouped)
        }).catch((err) => {
            toast.error("Network error: " + err)
        });
    }, []);

    const handleCheckboxChange = (name) => {
        setCheckedItems(prev =>
            prev.includes(name) ? prev.filter(i => i !== name) : [...prev, name]
        );
    };

    const checkedPre  = preItems.filter(i => checkedItems.includes(i.name)).length
    const checkedPost = postItems.filter(i => checkedItems.includes(i.name)).length
    const prePct  = preItems.length  > 0 ? Math.round((checkedPre  / preItems.length)  * 100) : 0
    const postPct = postItems.length > 0 ? Math.round((checkedPost / postItems.length) * 100) : 0

    const PRE_CATEGORY_ORDER = ['Atem', 'Directing', 'ProPresenter', 'Live Streaming', 'Stage Manager', 'Producer']
    const preCategories = Object.keys(categorizedItems)
        .filter(c => c !== POST_PRODUCTION)
        .sort((a, b) => {
            const ai = PRE_CATEGORY_ORDER.findIndex(x => x.toLowerCase() === a.toLowerCase())
            const bi = PRE_CATEGORY_ORDER.findIndex(x => x.toLowerCase() === b.toLowerCase())
            if (ai !== -1 && bi !== -1) return ai - bi
            if (ai !== -1) return -1
            if (bi !== -1) return 1
            return a.localeCompare(b)
        })
    const postCategories = Object.keys(categorizedItems).filter(c => c === POST_PRODUCTION)

    const ChecklistGrid = ({ categories }) => (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
        }}>
            {categories.map(category => (
                <div key={category} style={{
                    backgroundColor: '#f8f9fa',
                    borderRadius: '10px',
                    border: '1px solid #e0e0e0',
                    padding: '16px'
                }}>
                    <h4 style={{
                        color: '#ffa800',
                        margin: '0 0 12px',
                        fontSize: 'clamp(12px, 3.2vw, 13px)',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        borderBottom: '2px solid #ffa800',
                        paddingBottom: '4px',
                        display: 'inline-block'
                    }}><strong>{category}</strong></h4>
                    {categorizedItems[category].map((checkitem, index) => {
                        const isChecked = checkedItems.includes(checkitem.name)
                        return (
                            <label key={index} style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                padding: '7px 8px',
                                marginTop: '3px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: isChecked ? '#e8f8f0' : 'transparent',
                                transition: 'background-color 0.2s'
                            }}>
                                <input type="checkbox"
                                    style={{marginRight: '9px', marginTop: '2px', accentColor: '#35ab7c', width: '15px', height: '15px', flexShrink: 0}}
                                    onChange={() => handleCheckboxChange(checkitem.name)}
                                    checked={isChecked}
                                />
                                <span style={{
                                    fontSize: 'clamp(12px, 3.2vw, 13px)',
                                    color: isChecked ? '#35ab7c' : '#333',
                                    textDecoration: isChecked ? 'line-through' : 'none',
                                    lineHeight: '1.4'
                                }}>{checkitem.name}</span>
                            </label>
                        )
                    })}
                </div>
            ))}
        </div>
    )

    return(
        <Fragment>
            <Topbar/>

            <div className="hero-area">
                <div className="page-banner parallax" style={{backgroundColor:"#003165"}}>
                    <div className="container">
                        <div className="page-banner-text">
                            {isLoggedIn ?
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>Production Checklist</strong></h1>
                                :
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div className="spacer-20"></div>

            {isLoggedIn ? (
                <div id="main-container">
                    <div className="container" style={{padding: '20px'}}>

                        {/* ── Pre-production section ── */}
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            padding: '24px',
                            marginBottom: '30px'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '24px',
                                alignItems: 'flex-start'
                            }}>
                                {/* Chart */}
                                <div style={{flex: '0 0 min(100%, 360px)', width: 'min(100%, 360px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                                    <h3 style={{color: '#003165', margin: '0 0 12px', fontSize: 'clamp(14px, 3.5vw, 16px)', textAlign: 'center'}}><strong>Pre-Production Ready</strong></h3>
                                    <DonutChart percentage={prePct} />
                                    <div style={{marginTop: '8px', color: '#666', fontSize: '13px', textAlign: 'center'}}>
                                        {checkedPre} / {preItems.length} items
                                    </div>
                                </div>

                                {/* Checklist grid */}
                                <div style={{flex: '1 1 300px', minWidth: '0'}}>
                                    <h2 style={{color: '#003165', margin: '0 0 16px', fontSize: 'clamp(15px, 4vw, 17px)'}}><strong>Complete before go live</strong></h2>
                                    <ChecklistGrid categories={preCategories} />
                                </div>
                            </div>
                        </div>

                        {/* ── Post Production section ── */}
                        {postItems.length > 0 && (
                            <div style={{
                                backgroundColor: '#fff',
                                borderRadius: '12px',
                                border: '2px solid #003165',
                                padding: '24px',
                                marginBottom: '30px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '24px',
                                    alignItems: 'flex-start'
                                }}>
                                    {/* Chart */}
                                    <div style={{flex: '0 0 min(100%, 360px)', width: 'min(100%, 360px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                                        <h3 style={{color: '#003165', margin: '0 0 12px', fontSize: 'clamp(14px, 3.5vw, 16px)', textAlign: 'center'}}><strong>Post Production</strong></h3>
                                        <DonutChart percentage={postPct} color='#003165' bg='#d0dce8' label='Done' />
                                        <div style={{marginTop: '8px', color: '#666', fontSize: '13px', textAlign: 'center'}}>
                                            {checkedPost} / {postItems.length} items
                                        </div>
                                    </div>

                                    {/* Checklist grid */}
                                    <div style={{flex: '1 1 300px', minWidth: '0'}}>
                                        <h2 style={{color: '#003165', margin: '0 0 16px', fontSize: 'clamp(15px, 4vw, 17px)'}}><strong>Complete after service ends</strong></h2>
                                        <ChecklistGrid categories={postCategories} />
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            ) : <></>}
        </Fragment>
    )
}

const mapStateToProps = (state) => {
  return{
     isLoggedIn: state.authReducer.isLoggedIn,
     loggedInUser: state.authReducer.user
  };
};

const mapDispatchToProps = {
  logout
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductionReady);
